<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">
          <i class="fas fa-box"></i>
          Détails du Produit
        </h2>
        <button @click="$emit('close')" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div class="product-details">
          <!-- Informations principales -->
          <div class="detail-section" :class="{ 'section-collapsed': !expandedSections.general }">
            <h3 class="section-title" @click="toggleSection('general')">
              Informations Générales
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </h3>
            <div class="detail-grid" v-show="expandedSections.general">
              <div class="detail-item">
                <label>Référence Interne</label>
                <div class="detail-value reference">{{ product.referenceInterne }}</div>
              </div>
              <div class="detail-item">
                <label>Désignation</label>
                <div class="detail-value">{{ product.designation }}</div>
              </div>
              <div class="detail-item">
                <label>Catégorie</label>
                <div class="detail-value">
                  <span class="category-badge" :class="getCategoryClass(product.categorie)">
                    {{ product.categorie }}
                  </span>
                </div>
              </div>
              <div class="detail-item">
                <label>Statut</label>
                <div class="detail-value">
                  <span class="status-badge" :class="getStatusClass(product.statut)">
                    {{ getStatusLabel(product.statut) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Identification -->
          <div class="detail-section" :class="{ 'section-collapsed': !expandedSections.identification }" v-if="product.marque || product.modele || product.imei || product.iccid">
            <h3 class="section-title" @click="toggleSection('identification')">
              Identification
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </h3>
            <div class="detail-grid" v-show="expandedSections.identification">
              <div class="detail-item" v-if="product.marque">
                <label>Marque</label>
                <div class="detail-value">{{ product.marque }}</div>
              </div>
              <div class="detail-item" v-if="product.modele">
                <label>Modèle</label>
                <div class="detail-value">{{ product.modele }}</div>
              </div>
              <div class="detail-item" v-if="product.imei">
                <label>IMEI</label>
                <div class="detail-value identifier">{{ product.imei }}</div>
              </div>
              <div class="detail-item" v-if="product.iccid">
                <label>ICCID</label>
                <div class="detail-value identifier">{{ product.iccid }}</div>
              </div>
              <div class="detail-item" v-if="product.numeroSerie">
                <label>Numéro de Série</label>
                <div class="detail-value identifier">{{ product.numeroSerie }}</div>
              </div>
            </div>
          </div>

          <!-- Informations financières -->
          <div class="detail-section" :class="{ 'section-collapsed': !expandedSections.financier }">
            <h3 class="section-title" @click="toggleSection('financier')">
              Informations Financières
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </h3>
            <div class="detail-grid" v-show="expandedSections.financier">
              <div class="detail-item">
                <label>Prix d'Achat</label>
                <div class="detail-value price">{{ formatPrice(product.prixAchat) }}</div>
              </div>
              <div class="detail-item">
                <label>Prix de Vente HT</label>
                <div class="detail-value price">{{ formatPrice(product.prixVente) }}</div>
              </div>
              <div class="detail-item">
                <label>TVA</label>
                <div class="detail-value">{{ product.tva }}%</div>
              </div>
              <div class="detail-item">
                <label>Prix de Vente TTC</label>
                <div class="detail-value price highlight">{{ formatPrice(product.prixVenteTTC) }}</div>
              </div>
            </div>
          </div>

          <!-- Gestion de stock -->
          <div class="detail-section" :class="{ 'section-collapsed': !expandedSections.stock }">
            <h3 class="section-title" @click="toggleSection('stock')">
              Gestion de Stock
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </h3>
            <div class="detail-grid" v-show="expandedSections.stock">
              <div class="detail-item">
                <label>Emplacement</label>
                <div class="detail-value">{{ formatLocation(product.emplacement, product.emplacementNom) }}</div>
              </div>
              <div class="detail-item">
                <label>Seuil d'Alerte</label>
                <div class="detail-value">{{ product.seuilAlerte }} {{ product.unite }}</div>
              </div>
              <div class="detail-item">
                <label>Unité</label>
                <div class="detail-value">{{ product.unite }}</div>
              </div>
            </div>
          </div>

          <!-- Description -->
          <div class="detail-section" :class="{ 'section-collapsed': !expandedSections.description }" v-if="product.description">
            <h3 class="section-title" @click="toggleSection('description')">
              Description
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </h3>
            <div class="description" v-show="expandedSections.description">
              {{ product.description }}
            </div>
          </div>

          <!-- Informations de traçabilité -->
          <div class="detail-section" :class="{ 'section-collapsed': !expandedSections.tracabilite }">
            <h3 class="section-title" @click="toggleSection('tracabilite')">
              Traçabilité
              <i class="fas fa-chevron-down section-toggle-icon"></i>
            </h3>
            <div class="detail-grid" v-show="expandedSections.tracabilite">
              <div class="detail-item">
                <label>Créé par</label>
                <div class="detail-value">
                  {{ product.creeParUtilisateur ? 
                     `${product.creeParUtilisateur.prenom} ${product.creeParUtilisateur.nom}` : 
                     'N/A' }}
                </div>
              </div>
              <div class="detail-item">
                <label>Date de création</label>
                <div class="detail-value">{{ formatDate(product.dateCreation) }}</div>
              </div>
              <div class="detail-item" v-if="product.modifieParUtilisateur">
                <label>Modifié par</label>
                <div class="detail-value">
                  {{ `${product.modifieParUtilisateur.prenom} ${product.modifieParUtilisateur.nom}` }}
                </div>
              </div>
              <div class="detail-item" v-if="product.dateModification !== product.dateCreation">
                <label>Dernière modification</label>
                <div class="detail-value">{{ formatDate(product.dateModification) }}</div>
              </div>
            </div>
          </div>

          <!-- Garantie -->
          <div class="detail-section" v-if="product.garantie && product.garantie.duree">
            <h3 class="section-title">Garantie</h3>
            <div class="detail-grid">
              <div class="detail-item">
                <label>Durée</label>
                <div class="detail-value">{{ product.garantie.duree }} mois</div>
              </div>
              <div class="detail-item" v-if="product.garantie.dateDebut">
                <label>Date de début</label>
                <div class="detail-value">{{ formatDate(product.garantie.dateDebut) }}</div>
              </div>
              <div class="detail-item" v-if="product.garantie.dateFin">
                <label>Date de fin</label>
                <div class="detail-value">{{ formatDate(product.garantie.dateFin) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="generateBarcode" class="btn btn-secondary">
          <i class="fas fa-barcode"></i>
          Code-barres
        </button>
        <button @click="generateQRCode" class="btn btn-secondary">
          <i class="fas fa-qrcode"></i>
          QR Code
        </button>
        <button @click="$emit('close')" class="btn btn-primary">
          Fermer
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductDetailModal',
  props: {
    product: {
      type: Object,
      required: true
    }
  },
  emits: ['close'],
  setup() {
    const { ref } = require('vue')
    
    // État des sections (toutes fermées par défaut)
    const expandedSections = ref({
      general: false,
      identification: false,
      financier: false,
      stock: false,
      description: false,
      tracabilite: false
    })

    // Fonction pour basculer une section
    const toggleSection = (sectionName) => {
      expandedSections.value[sectionName] = !expandedSections.value[sectionName]
    }
    
    const getCategoryClass = (categorie) => {
      const classes = {
        'GPS': 'category-gps',
        'SIM': 'category-sim',
        'Accessoire': 'category-accessoire',
        'Câble': 'category-cable',
        'Capteur': 'category-capteur',
        'Alimentation': 'category-alimentation',
        'Divers': 'category-divers'
      }
      return classes[categorie] || 'category-default'
    }

    const getStatusClass = (statut) => {
      const classes = {
        'en_stock': 'status-available',
        'affecte_technicien': 'status-assigned',
        'installe': 'status-installed',
        'retourne': 'status-returned',
        'defectueux': 'status-defective',
        'perdu': 'status-lost'
      }
      return classes[statut] || 'status-default'
    }

    const getStatusLabel = (statut) => {
      const labels = {
        'en_stock': 'En stock',
        'affecte_technicien': 'Affecté technicien',
        'installe': 'Installé',
        'retourne': 'Retourné',
        'defectueux': 'Défectueux',
        'perdu': 'Perdu'
      }
      return labels[statut] || statut
    }

    const formatLocation = (emplacement, emplacementNom = null) => {
      // Si on a le nom enrichi du backend, l'utiliser en priorité
      if (emplacementNom) {
        return emplacementNom
      }
      
      if (!emplacement) return '-'
      
      // Formats technicien
      if (emplacement.startsWith('sac_technicien_') || 
          emplacement.startsWith('Technicien ') || 
          emplacement.startsWith('Tech:') ||
          emplacement.startsWith('Tech ')) {
        return 'Technicien'
      }
      
      // Formats client
      if (emplacement.startsWith('chez_client_') || 
          emplacement.startsWith('client_') ||
          emplacement.startsWith('Client ') ||
          emplacement.startsWith('Client:')) {
        return 'Chez Client'
      }
      
      // Magasin central
      if (emplacement === 'magasin_central' || emplacement === 'Magasin Central') {
        return 'Magasin Central'
      }
      
      return emplacement.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    const formatPrice = (price) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'TND'
      }).format(price || 0)
    }

    const formatDate = (date) => {
      if (!date) return 'N/A'
      return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(date))
    }

    const generateBarcode = () => {
      // TODO: Implémenter la génération de code-barres
      console.log('Génération code-barres')
    }

    const generateQRCode = () => {
      // TODO: Implémenter la génération de QR code
      console.log('Génération QR code')
    }

    return {
      expandedSections,
      toggleSection,
      getCategoryClass,
      getStatusClass,
      getStatusLabel,
      formatLocation,
      formatPrice,
      formatDate,
      generateBarcode,
      generateQRCode
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
}

.modal-title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.25rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background-color 0.3s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.product-details {
  padding: 1.5rem;
}

.detail-section {
  margin-bottom: 2rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8));
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-left: 4px solid var(--primary-blue);
  overflow: hidden;
  transition: all 0.3s ease;
  padding: 1.5rem;
}

.detail-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 27, 183, 0.3);
}

.detail-section:last-child {
  margin-bottom: 0;
}

.section-collapsed {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.section-collapsed:hover {
  transform: translateY(0);
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--primary-blue);
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(0, 27, 183, 0.2);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
  position: relative;
}

.section-title:hover {
  color: var(--secondary-blue);
}

.section-toggle-icon {
  margin-left: auto;
  color: var(--primary-blue);
  font-size: 1.2rem;
  transition: transform 0.3s ease;
}

.section-collapsed .section-toggle-icon {
  transform: rotate(-90deg);
}

.detail-grid,
.description {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-item label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 1rem;
  color: #495057;
  padding: 0.5rem 0;
}

.detail-value.reference {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: var(--primary-blue);
  font-size: 1.1rem;
}

.detail-value.identifier {
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  background: #f8f9fa;
  padding: 0.5rem;
  border-radius: 4px;
  border-left: 3px solid var(--primary-blue);
}

.detail-value.price {
  font-weight: 600;
  color: var(--secondary-blue);
  font-size: 1.1rem;
}

.detail-value.price.highlight {
  color: var(--primary-blue);
  font-size: 1.2rem;
}

.description {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid var(--primary-blue);
  line-height: 1.6;
  color: #495057;
}

/* Badges */
.category-badge, .status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Badges catégories */
.category-gps { background: #e3f2fd; color: #1976d2; }
.category-sim { background: #f3e5f5; color: #7b1fa2; }
.category-accessoire { background: #e8f5e8; color: #388e3c; }
.category-cable { background: #fff3e0; color: #f57c00; }
.category-capteur { background: #fce4ec; color: #c2185b; }
.category-alimentation { background: #e0f2f1; color: #00796b; }
.category-divers { background: #f5f5f5; color: #616161; }

/* Badges statuts */
.status-available { background: #d4edda; color: #155724; }
.status-assigned { background: #cce5ff; color: #004085; }
.status-installed { background: #d1ecf1; color: #0c5460; }
.status-returned { background: #f8d7da; color: #721c24; }
.status-defective { background: #f5c6cb; color: #721c24; }
.status-lost { background: #343a40; color: white; }

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border: 1px solid rgba(0, 27, 183, 0.3);
}

.btn-secondary {
  background: linear-gradient(135deg, #6c757d, #495057);
  color: white;
  border: 1px solid rgba(108, 117, 125, 0.3);
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Responsive */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 1rem;
  }
  
  .modal-content {
    max-height: 95vh;
  }
  
  .detail-grid {
    grid-template-columns: 1fr;
  }
  
  .modal-header {
    padding: 1rem;
  }
  
  .modal-title {
    font-size: 1.1rem;
  }
  
  .product-details {
    padding: 1rem;
  }
  
  .modal-footer {
    padding: 1rem;
    flex-wrap: wrap;
  }
  
  .btn {
    flex: 1;
    justify-content: center;
  }
}
</style>
